import { CartUpdateEvent } from '@theme/events';

/**
 * Cart rewards controller.
 * - Auto-adds/removes the Free Mystery Gift product when the cart crosses the
 *   threshold rendered on [data-cart-rewards] (subtotal excludes the gift itself).
 * - Powers the one-tap creatine upsell button ([data-upsell-add]).
 *
 * If a shopper manually removes the gift, we remember that for the session and
 * don't force it back until they drop below and re-cross the threshold.
 */
const GIFT_STATE_KEY = 'ipCartGiftState';
const CHECKOUT_UPSELL_KEY = 'ipCheckoutUpsellShown';
const SHIP_CUTOFF_HOUR_ET = 14; // 2 p.m. Eastern

class CartRewards {
  #busy = false;
  #checkoutTrigger = null;

  constructor() {
    // On the cart page the drawer must not auto-open over the page whenever
    // quantities change (the theme opens it on every cart:update event).
    if (window.location.pathname.endsWith('/cart')) {
      document
        .querySelectorAll('cart-drawer-component[auto-open]')
        .forEach((el) => el.removeAttribute('auto-open'));
    }

    document.addEventListener('cart:update', this.#onCartUpdate);
    document.addEventListener('click', this.#onClick);
    this.#syncGift();
    this.#updateShipCutoff();
    setInterval(() => this.#updateShipCutoff(), 60000);

    this.#placeCardReviews();
    document.addEventListener('shopify:section:load', () => this.#placeCardReviews());
  }

  /**
   * Product cards whose title/price live inside a group block can't be
   * reordered with flex `order` on the card container — move the star rating
   * to sit directly above the price inside the group instead.
   */
  #placeCardReviews() {
    document.querySelectorAll('.product-card__content').forEach((card) => {
      const reviews = card.querySelector(':scope > .product-card__reviews');
      const group = card.querySelector(':scope > .group-block');
      if (!reviews || !group) return;
      const price = group.querySelector('product-price');
      if (!price || !price.parentElement) return;
      price.parentElement.insertBefore(reviews, price);
    });
  }

  #onCartUpdate = (event) => {
    // Morphs replace the cutoff element; re-populate it once the DOM settles.
    setTimeout(() => this.#updateShipCutoff(), 400);
    const source = event.detail?.data?.source ?? '';
    if (source.startsWith('cart-rewards')) return;
    this.#syncGift();
  };

  #updateShipCutoff() {
    const els = document.querySelectorAll('[data-ship-cutoff]');
    if (!els.length) return;

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const weekday = get('weekday');
    const hour = Number(get('hour')) % 24;
    const minute = Number(get('minute'));

    const isWeekday = weekday !== 'Sat' && weekday !== 'Sun';
    let text;
    if (isWeekday && hour < SHIP_CUTOFF_HOUR_ET) {
      const minutesLeft = SHIP_CUTOFF_HOUR_ET * 60 - (hour * 60 + minute);
      const h = Math.floor(minutesLeft / 60);
      const m = minutesLeft % 60;
      const window = h > 0 ? `${h}h ${m}m` : `${m}m`;
      text = `Order within ${window} — ships today`;
    } else {
      text = 'Order now — ships next business day';
    }

    els.forEach((el) => {
      el.textContent = text;
      el.hidden = false;
    });
  }

  #onClick = (event) => {
    const checkoutButton = event.target.closest('.cart__checkout-button');
    if (checkoutButton && this.#interceptCheckout(checkoutButton, event)) return;

    const button = event.target.closest('[data-upsell-add]');
    if (button) this.#addUpsell(button);
  };

  /**
   * In the drawer, the modal sits inside the drawer's own <dialog>, whose
   * keydown/click handlers close the drawer on Esc and on clicks outside its
   * bounding box (which is what a click on the modal's backdrop is). Handle
   * the modal's events on the dialog itself and keep them from bubbling out.
   */
  #wireDialog(dialog) {
    if (dialog.dataset.upsellWired) return;
    dialog.dataset.upsellWired = 'true';

    // If the drawer somehow closes underneath us, an open modal inside it
    // becomes invisible while still making the page inert — never allow that.
    const host = dialog.parentElement?.closest('dialog');
    host?.addEventListener('close', () => {
      if (dialog.open) dialog.close();
    });

    dialog.addEventListener('keydown', (event) => {
      event.stopPropagation();
      // Stopping propagation also hides the key from the browser's dialog
      // close-watcher, so handle Esc ourselves.
      if (event.key === 'Escape') {
        event.preventDefault();
        dialog.close();
      }
    });
    dialog.addEventListener('click', (event) => {
      event.stopPropagation();

      const add = event.target.closest('[data-checkout-upsell-add]');
      if (add) {
        this.#addCheckoutUpsell(add);
        return;
      }
      if (event.target.closest('[data-checkout-upsell-decline]')) {
        this.#resumeCheckout();
        return;
      }
      // Close button, or a click on the dialog element itself (= backdrop).
      if (event.target.closest('[data-checkout-upsell-close]') || event.target === dialog) {
        dialog.close();
      }
    });
  }

  /**
   * Shows the last-chance upsell dialog instead of submitting the cart form.
   * Fires at most once per session; "No thanks" and "Add" both resume the
   * checkout, while the close button / Esc / backdrop keeps the shopper in
   * the cart. Returns true when the checkout click was intercepted — any
   * failure returns false before preventDefault so checkout is never blocked.
   */
  #interceptCheckout(button, event) {
    const dialog = button.closest('.cart__ctas')?.querySelector('dialog[data-checkout-upsell]');
    if (!dialog || dialog.open) return false;

    try {
      if (sessionStorage.getItem(CHECKOUT_UPSELL_KEY)) return false;
    } catch {
      return false;
    }

    this.#wireDialog(dialog);
    try {
      dialog.showModal();
    } catch {
      return false;
    }

    event.preventDefault();
    this.#checkoutTrigger = button;
    try {
      sessionStorage.setItem(CHECKOUT_UPSELL_KEY, '1');
    } catch {
      /* storage gone mid-session — worst case the popup shows again */
    }
    return true;
  }

  async #addCheckoutUpsell(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled) return;

    // Freeze the whole modal so "No thanks" can't race the in-flight add.
    const dialog = button.closest('dialog');
    dialog?.querySelectorAll('button').forEach((el) => (el.disabled = true));
    button.textContent = 'Adding…';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.#sectionIds(),
        }),
      });

      if (!response.ok) {
        console.error('cart-rewards: checkout upsell add failed', await response.text());
        this.#resumeCheckout();
        return;
      }

      const data = await response.json();
      // Morph the cart UI before leaving so a back-button return doesn't show
      // stale totals, and give the add a chance to pull the mystery gift
      // across its threshold before hand-off.
      await this.#notify(data, 'cart-rewards-checkout-upsell');
      await this.#syncGift();
      this.#resumeCheckout();
    } catch (error) {
      console.error('cart-rewards: checkout upsell add failed', error);
      this.#resumeCheckout();
    }
  }

  #resumeCheckout() {
    document.querySelectorAll('dialog[data-checkout-upsell]').forEach((dialog) => {
      if (dialog.open) dialog.close();
    });

    const trigger = this.#checkoutTrigger;
    // requestSubmit() with a submitter fires the submit event without a click,
    // so #interceptCheckout doesn't run again. Safari < 16 has no
    // requestSubmit — fall back to navigating straight to checkout.
    if (trigger?.form && trigger.isConnected && typeof trigger.form.requestSubmit === 'function') {
      trigger.form.requestSubmit(trigger);
    } else {
      window.location.href = '/checkout';
    }
  }

  #sectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  async #fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  #getGiftState() {
    try {
      return sessionStorage.getItem(GIFT_STATE_KEY);
    } catch {
      return null;
    }
  }

  #setGiftState(value) {
    try {
      if (value === null) {
        sessionStorage.removeItem(GIFT_STATE_KEY);
      } else {
        sessionStorage.setItem(GIFT_STATE_KEY, value);
      }
    } catch {
      /* storage unavailable — gift may re-add after manual removal */
    }
  }

  async #syncGift() {
    if (this.#busy) return;

    const bar = document.querySelector('[data-cart-rewards]');
    if (!bar) return;

    const variantId = Number(bar.dataset.giftVariantId);
    const threshold = Number(bar.dataset.giftThreshold);
    if (!variantId || !threshold) return;

    this.#busy = true;
    try {
      const cart = await this.#fetchCart();
      const giftLine = cart.items.find((item) => item.variant_id === variantId);
      const subtotal = cart.total_price - (giftLine ? giftLine.final_line_price : 0);
      const sections = this.#sectionIds();
      const state = this.#getGiftState();

      let endpoint = null;
      let payload = null;

      if (subtotal >= threshold) {
        if (!giftLine) {
          if (state === 'added') {
            // We added it and now it's gone: shopper removed it on purpose.
            this.#setGiftState('dismissed');
          } else if (state !== 'dismissed') {
            endpoint = '/cart/add.js';
            payload = { items: [{ id: variantId, quantity: 1 }], sections };
            this.#setGiftState('added');
          }
        } else if (giftLine.quantity !== 1) {
          endpoint = '/cart/change.js';
          payload = { id: giftLine.key, quantity: 1, sections };
        }
      } else {
        this.#setGiftState(null);
        if (giftLine) {
          endpoint = '/cart/change.js';
          payload = { id: giftLine.key, quantity: 0, sections };
        }
      }

      if (!endpoint) return;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        if (endpoint === '/cart/add.js') this.#setGiftState(null);
        return;
      }

      const data = await response.json();
      await this.#notify(data, 'cart-rewards');
    } catch (error) {
      console.error('cart-rewards: gift sync failed', error);
    } finally {
      this.#busy = false;
    }
  }

  async #addUpsell(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled) return;

    button.disabled = true;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.#sectionIds(),
        }),
      });

      if (!response.ok) {
        console.error('cart-rewards: upsell add failed', await response.text());
        return;
      }

      const data = await response.json();
      await this.#notify(data, 'cart-rewards-upsell');
      // Adding the upsell may push the cart across the gift threshold.
      await this.#syncGift();
    } catch (error) {
      console.error('cart-rewards: upsell add failed', error);
    } finally {
      button.disabled = false;
    }
  }

  async #notify(data, source) {
    const cart = await this.#fetchCart();
    document.dispatchEvent(
      new CartUpdateEvent(cart, source, {
        source,
        itemCount: cart.item_count,
        sections: data.sections ?? {},
      })
    );
  }
}

if (!window.cartRewards) {
  window.cartRewards = new CartRewards();
}
